import React, { useContext, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { GlobalContext, TenantSettingsContext } from '../../App'
import { openDemoPersonaPicker } from './DemoExperience'

// Compact, always-pulsing "Switch User" pill for demo tenants. Renders null
// unless tenantInfo?.isDemo, so callers (MainFrame header + every portal
// layout) can mount it unconditionally.
//
// tenantInfo is read from TenantSettingsContext first (it wraps every tree,
// including the applicant/client/hospital portals) with GlobalContext and an
// optional prop as fallbacks, so the button works anywhere a logged-in demo
// session can land.

// Expanding, fading box-shadow ring (~2s loop). The colours are supplied per
// element via CSS variables so the keyframes can stay static while still
// using the theme's primary colour.
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 var(--demo-pulse-start);
  }
  70% {
    box-shadow: 0 0 0 12px var(--demo-pulse-end);
  }
  100% {
    box-shadow: 0 0 0 0 var(--demo-pulse-end);
  }
`

// Gentle vertical bob for the first-visit coach mark callout.
const coachBounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(4px);
  }
`

// First-visit coach mark: shown once per visitor (localStorage), dismissed by
// its close button, by clicking the switch button, or after ~8 seconds.
const COACH_MARK_KEY = 'ambunet.demo.switchSeen'

// The button is mounted in the MainFrame header and every portal layout — if
// more than one instance is ever on screen, this module-level flag ensures
// only the first to mount shows the callout.
let coachMarkClaimed = false

const hasSeenCoachMark = () => {
  try {
    return !!window.localStorage.getItem(COACH_MARK_KEY)
  } catch (e) {
    // localStorage unavailable — never show rather than show forever.
    return true
  }
}

const markCoachMarkSeen = () => {
  try {
    window.localStorage.setItem(COACH_MARK_KEY, '1')
  } catch (e) {
    // Non-fatal — the session-level claim still suppresses it.
  }
}

const DemoSwitchButton = ({ tenantInfo: tenantInfoProp, floating = false, sx = {} }) => {
  const tenantSettings = useContext(TenantSettingsContext)
  const globalContext = useContext(GlobalContext)
  const theme = useTheme()
  const buttonRef = useRef(null)
  const [coachOpen, setCoachOpen] = useState(false)

  const tenantInfo =
    tenantInfoProp ?? tenantSettings?.tenantInfo ?? globalContext?.tenantInfo
  const isDemo = !!tenantInfo?.isDemo

  useEffect(() => {
    if (!isDemo || coachMarkClaimed || hasSeenCoachMark()) return

    coachMarkClaimed = true
    setCoachOpen(true)

    // Auto-dismiss after ~8s. Deliberately does NOT set the localStorage
    // key — only an explicit click/close marks it as seen for good.
    const timer = setTimeout(() => setCoachOpen(false), 8000)
    return () => clearTimeout(timer)
  }, [isDemo])

  if (!isDemo) return null

  const dismissCoachMark = () => {
    setCoachOpen(false)
    markCoachMarkSeen()
  }

  const handleClick = () => {
    // Once the visitor has used the button, never coach them again.
    setCoachOpen(false)
    markCoachMarkSeen()
    openDemoPersonaPicker()
  }

  const ringColour = theme.palette.primary.main

  return (
    <>
      <Tooltip title="Switch demo user">
        <Button
          ref={buttonRef}
          variant="contained"
          color="primary"
          size="small"
          disableElevation
          startIcon={<SwapHorizIcon />}
          onClick={handleClick}
          sx={{
            '--demo-pulse-start': alpha(ringColour, 0.5),
            '--demo-pulse-end': alpha(ringColour, 0),
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 800,
            whiteSpace: 'nowrap',
            minWidth: 0,
            px: { xs: 1.25, sm: 2 },
            animation: `${pulse} 2s ease-out infinite`,
            // Icon-only below the sm breakpoint.
            '& .MuiButton-startIcon': { ml: 0, mr: { xs: 0, sm: 1 } },
            ...(floating && {
              position: 'fixed',
              top: 12,
              right: 12,
              zIndex: theme.zIndex.appBar + 1,
            }),
            ...sx,
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Switch User
          </Box>
        </Button>
      </Tooltip>

      <Popper
        open={coachOpen}
        anchorEl={buttonRef.current}
        placement="bottom-end"
        transition
        sx={{ zIndex: theme.zIndex.tooltip }}
        modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper
              elevation={6}
              sx={{
                position: 'relative',
                px: 1.5,
                py: 1,
                maxWidth: 240,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                animation: `${coachBounce} 1.8s ease-in-out infinite`,
              }}
            >
              {/* Arrow pointing up at the button. */}
              <Box
                sx={{
                  position: 'absolute',
                  top: -5,
                  right: 20,
                  width: 12,
                  height: 12,
                  bgcolor: 'primary.main',
                  transform: 'rotate(45deg)',
                }}
              />
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <Typography variant="body2" fontWeight={700}>
                  Click here to change views / users
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Dismiss"
                  onClick={dismissCoachMark}
                  sx={{ color: 'inherit', p: 0.25, mt: -0.25 }}
                >
                  <CloseRoundedIcon fontSize="inherit" />
                </IconButton>
              </Stack>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  )
}

export default DemoSwitchButton
